import React from "react";
import {
  Box,
  Paper,
  Typography,
  Button,
  IconButton,
  Alert,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from "@mui/material";
import { ArrowBack, Print, EventBusy } from "@mui/icons-material";
import { useParams, useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import useEffectiveRolePath from "../../../hooks/useEffectiveRolePath";
import { useTimetableById } from "../../../hooks/queries/useTimetable";

/**
 * PDFStyleTimetableView
 *
 * Renders a generated timetable in the reference "PDF" layout:
 *   - Days (MON->SUN, only those present in slots) as ROWS.
 *   - Lecture time windows + break windows as COLUMNS, ordered by start time.
 *   - Recess/break columns (from scheduleTemplate.breaks) span all day rows.
 *   - Cells show SUBJECT_CODE (THEORY) or SUBJECT_CODE:BATCH (PRACTICAL); parallel
 *     batch entries stack in the same cell.
 *
 * Data comes from the existing detail hook (useTimetableById -> GET /timetable-generation/:id),
 * which surfaces an additive `scheduleTemplate` object with `breaks[]` of {start,end,label}.
 *
 * No-Crash Guarantee: all external data access uses optional chaining, collections are
 * checked before iteration, and reassigned destructures use `let`. The view never throws
 * on missing subject code, missing teacher, or null practicalBatchCode.
 *
 * Task 2.1 = base grid + header. Task 2.2 = print CSS (landscape @page) + Print button.
 * Task 2.3 = defensive/empty-state rendering. All in this single file.
 */

// MON->SUN canonical ordering. Only days present in slots are rendered.
const DAY_ORDER = [
  "MONDAY",
  "TUESDAY",
  "WEDNESDAY",
  "THURSDAY",
  "FRIDAY",
  "SATURDAY",
  "SUNDAY",
];

const DAY_SHORT = {
  MONDAY: "MON",
  TUESDAY: "TUE",
  WEDNESDAY: "WED",
  THURSDAY: "THU",
  FRIDAY: "FRI",
  SATURDAY: "SAT",
  SUNDAY: "SUN",
};

/** Print stylesheet: landscape @page + chrome hiding + solid borders. @page cannot be
 *  expressed via MUI sx, so it lives in a component-scoped <style> block. */
const PRINT_STYLES = `
@media print {
  @page { size: landscape; margin: 12mm; }
  body { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
  .pdf-tt-no-print { display: none !important; }
  .pdf-tt-sheet {
    box-shadow: none !important;
    border: none !important;
    padding: 0 !important;
    width: 100% !important;
  }
  .pdf-tt-sheet table { width: 100% !important; }
  .pdf-tt-sheet td, .pdf-tt-sheet th {
    border: 1px solid #000 !important;
  }
}
`;

/** Normalise an HH:mm[:ss] string to "HH:mm". Safe on null/garbage. */
const toHHmm = (value) => {
  if (!value || typeof value !== "string") return "";
  return value.trim().slice(0, 5);
};

/** Convert "HH:mm" to minutes-since-midnight for sorting. Safe on garbage. */
const timeToMinutes = (value) => {
  const hhmm = toHHmm(value);
  if (!hhmm) return Number.POSITIVE_INFINITY; // sort unknowns last
  const parts = hhmm.split(":");
  const h = Number.parseInt(parts[0], 10);
  const m = Number.parseInt(parts[1], 10);
  if (Number.isNaN(h) || Number.isNaN(m)) return Number.POSITIVE_INFINITY;
  return h * 60 + m;
};

const normalizeDay = (value) => String(value ?? "").trim().toUpperCase();

/** SUBJECT_CODE (theory) or SUBJECT_CODE:BATCH (practical). Fully null-safe. */
const buildCellLabel = (slot) => {
  const subject = slot?.subject || {};
  const code = subject?.code ?? subject?.name ?? "-";
  const type = String(slot?.lectureType ?? "").toUpperCase();
  const batch = slot?.practicalBatchCode;
  if (type === "PRACTICAL" && batch) {
    return `${code}:${batch}`;
  }
  return String(code);
};

/**
 * Build the column axis: union of lecture windows (from slots) + break windows
 * (from scheduleTemplate.breaks), sorted by start time.
 */
const buildColumns = (slots, breaks) => {
  const columns = [];
  const seen = new Set();

  const pushColumn = (col) => {
    const start = toHHmm(col?.start);
    const end = toHHmm(col?.end);
    const key = `${col?.type || "lecture"}|${start}|${end}`;
    if (seen.has(key)) return;
    seen.add(key);
    columns.push({ ...col, start, end, key });
  };

  if (Array.isArray(slots)) {
    slots.forEach((slot) => {
      const start = toHHmm(slot?.startTime);
      const end = toHHmm(slot?.endTime);
      if (!start) return;
      pushColumn({ type: "lecture", start, end });
    });
  }

  if (Array.isArray(breaks)) {
    breaks.forEach((brk) => {
      const start = toHHmm(brk?.start);
      const end = toHHmm(brk?.end);
      if (!start) return;
      pushColumn({
        type: "break",
        start,
        end,
        label: brk?.label || "RECESS",
      });
    });
  }

  columns.sort((a, b) => {
    const diff = timeToMinutes(a.start) - timeToMinutes(b.start);
    if (diff !== 0) return diff;
    return timeToMinutes(a.end) - timeToMinutes(b.end);
  });

  return columns;
};

/** Ordered list of days actually present in slots, MON->SUN. */
const buildDayRows = (slots) => {
  const present = new Set();
  if (Array.isArray(slots)) {
    slots.forEach((slot) => {
      const day = normalizeDay(slot?.dayOfWeek);
      if (day) present.add(day);
    });
  }
  return DAY_ORDER.filter((d) => present.has(d));
};

/** Human-readable "HH:mm - HH:mm" window label. */
const windowLabel = (col) => {
  const start = toHHmm(col?.start);
  const end = toHHmm(col?.end);
  if (start && end) return `${start} - ${end}`;
  return start || end || "";
};

const PDFStyleTimetableView = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { rolePath } = useEffectiveRolePath();

  const selectedInstitute = useSelector(
    (state) => state.instituteContext?.selectedInstitute
  );
  const user = useSelector((state) => state.auth?.user);

  const { data: detail, isLoading, error } = useTimetableById(id);

  const slots = React.useMemo(
    () => (Array.isArray(detail?.slots) ? detail.slots : []),
    [detail?.slots]
  );

  const breaks = React.useMemo(() => {
    const fromTemplate = detail?.scheduleTemplate?.breaks;
    return Array.isArray(fromTemplate) ? fromTemplate : [];
  }, [detail?.scheduleTemplate]);

  const columns = React.useMemo(
    () => buildColumns(slots, breaks),
    [slots, breaks]
  );

  const dayRows = React.useMemo(() => buildDayRows(slots), [slots]);

  const dayGrid = React.useMemo(() => {
    const grid = {};
    dayRows.forEach((day) => {
      grid[day] = {};
      columns.forEach((col) => {
        grid[day][col.key] = [];
      });
    });

    if (Array.isArray(slots)) {
      slots.forEach((slot) => {
        const day = normalizeDay(slot?.dayOfWeek);
        if (!grid[day]) return;
        const start = toHHmm(slot?.startTime);
        const end = toHHmm(slot?.endTime);
        const col = columns.find(
          (c) => c.type === "lecture" && c.start === start && c.end === end
        );
        if (col && Array.isArray(grid[day][col.key])) {
          grid[day][col.key].push(slot);
        }
      });
    }

    return grid;
  }, [slots, columns, dayRows]);

  const instituteName =
    selectedInstitute?.name ||
    user?.institute?.name ||
    user?.instituteName ||
    "";
  const departmentName = detail?.department?.name || "";
  const timetableName = detail?.name || "Timetable";
  const academicYear = detail?.academicYear || "";
  const semester = detail?.semester;

  const handlePrint = () => {
    try {
      window.print();
    } catch (e) {
      console.error("[PDFStyleTimetableView] print failed:", e?.message || e);
    }
  };

  const handleBack = () => {
    try {
      navigate(`${rolePath}/timetable`);
    } catch (e) {
      console.error("[PDFStyleTimetableView] navigate failed:", e?.message || e);
    }
  };

  if (isLoading) {
    return (
      <Box
        display="flex"
        flexDirection="column"
        justifyContent="center"
        alignItems="center"
        minHeight="400px"
        gap={2}
      >
        <CircularProgress size={48} />
        <Typography variant="body2" color="text.secondary">
          Loading timetable...
        </Typography>
      </Box>
    );
  }

  if (error || !detail) {
    return (
      <Alert severity="error" sx={{ m: 2 }}>
        Failed to load timetable: {error?.message || "Timetable not found"}
      </Alert>
    );
  }

  const hasSlots = slots.length > 0;

  return (
    <Box>
      {/* Print-only stylesheet: landscape @page + chrome hiding. */}
      <style>{PRINT_STYLES}</style>

      {/* Toolbar (hidden on print via .pdf-tt-no-print). */}
      <Box
        className="pdf-tt-no-print"
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 2,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <IconButton onClick={handleBack} aria-label="Back to timetables">
            <ArrowBack />
          </IconButton>
          <Typography variant="h5" fontWeight={600}>
            PDF View
          </Typography>
        </Box>
        <Button variant="contained" startIcon={<Print />} onClick={handlePrint}>
          Print / Download PDF
        </Button>
      </Box>

      {/* Printable sheet. */}
      <Paper variant="outlined" className="pdf-tt-sheet" sx={{ p: { xs: 2, md: 3 } }}>
        {/* Header block: institute / department + academic-year / semester line. */}
        <Box sx={{ textAlign: "center", mb: 2 }}>
          {instituteName ? (
            <Typography variant="h6" fontWeight={700} sx={{ textTransform: "uppercase" }}>
              {instituteName}
            </Typography>
          ) : null}
          {departmentName ? (
            <Typography variant="subtitle1" fontWeight={600}>
              Department of {departmentName}
            </Typography>
          ) : null}
          <Typography variant="subtitle2" fontWeight={600}>
            {timetableName}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {academicYear ? `A.Y. ${academicYear}` : ""}
            {semester != null && semester !== "" ? ` | Semester ${semester}` : ""}
          </Typography>
        </Box>

        {!hasSlots ? (
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              gap: 1,
              py: 6,
              color: "text.secondary",
            }}
          >
            <EventBusy sx={{ fontSize: 48, opacity: 0.5 }} />
            <Typography variant="subtitle1" fontWeight={600}>
              No scheduled slots
            </Typography>
            <Typography variant="body2" color="text.secondary" align="center">
              This timetable has no lectures to display yet. Generate or add slots to
              see the PDF layout.
            </Typography>
          </Box>
        ) : (
          <TableContainer>
            <Table
              size="small"
              sx={{
                borderCollapse: "collapse",
                "& td, & th": {
                  border: "1px solid",
                  borderColor: "divider",
                  textAlign: "center",
                  verticalAlign: "middle",
                },
              }}
            >
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 700, bgcolor: "grey.100", minWidth: 64 }}>
                    Day / Time
                  </TableCell>
                  {columns.map((col) =>
                    col.type === "break" ? (
                      <TableCell
                        key={col.key}
                        sx={{ fontWeight: 700, bgcolor: "warning.light", whiteSpace: "nowrap" }}
                      >
                        <Box>{col.label}</Box>
                        <Typography variant="caption" color="text.secondary">
                          {windowLabel(col)}
                        </Typography>
                      </TableCell>
                    ) : (
                      <TableCell
                        key={col.key}
                        sx={{ fontWeight: 700, bgcolor: "grey.100", whiteSpace: "nowrap" }}
                      >
                        {windowLabel(col)}
                      </TableCell>
                    )
                  )}
                </TableRow>
              </TableHead>
              <TableBody>
                {dayRows.map((day) => (
                  <TableRow key={day}>
                    <TableCell sx={{ fontWeight: 700, bgcolor: "grey.50" }}>
                      {DAY_SHORT[day] || day}
                    </TableCell>
                    {columns.map((col) => {
                      if (col.type === "break") {
                        return (
                          <TableCell
                            key={`${day}-${col.key}`}
                            sx={{ bgcolor: "warning.light", color: "warning.dark", fontWeight: 600 }}
                          >
                            {col.label}
                          </TableCell>
                        );
                      }

                      const cellSlots = Array.isArray(dayGrid?.[day]?.[col.key])
                        ? dayGrid[day][col.key]
                        : [];

                      return (
                        <TableCell key={`${day}-${col.key}`} sx={{ p: 0.75 }}>
                          {cellSlots.length === 0 ? (
                            <Typography variant="body2" color="text.disabled">
                              -
                            </Typography>
                          ) : (
                            <Box
                              sx={{
                                display: "flex",
                                flexDirection: "column",
                                gap: 0.25,
                                alignItems: "center",
                              }}
                            >
                              {cellSlots.map((slot, idx) => (
                                <Typography
                                  key={
                                    slot?.id ??
                                    `${day}-${col.key}-${slot?.practicalBatchCode ?? idx}`
                                  }
                                  variant="body2"
                                  fontWeight={600}
                                  sx={{ whiteSpace: "nowrap" }}
                                >
                                  {buildCellLabel(slot)}
                                </Typography>
                              ))}
                            </Box>
                          )}
                        </TableCell>
                      );
                    })}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Paper>
    </Box>
  );
};

export default PDFStyleTimetableView;
